import { useId, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { CircleSlash, ShieldCheck, User, UserCheck } from 'lucide-react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  SegmentedControl,
  Select,
} from '@/components/ui'
import { useUsersList } from '@/features/users/hooks/useUsers'
import { usePermissions } from '@/hooks/usePermissions'
import { userSubline } from '@/lib/formatters'
import type {
  CompanyMember,
  AddCompanyMemberPayload,
} from '@/types/company.types'
import { useAddCompanyMember } from '../hooks/useCompanies'
import { addCompanyMemberSchema, type AddCompanyMemberFormValues } from '../schemas/company.schemas'

export interface AddCompanyMemberModalProps {
  companyId: string
  open: boolean
  onClose: () => void
  onSuccess: () => void
  existingMembers: CompanyMember[]
}

function getMemberUserIds(members: CompanyMember[]): string[] {
  return members.map((m) =>
    typeof m.userId === 'string' ? m.userId : (m.userId as { _id: string })._id
  )
}

export function AddCompanyMemberModal({
  companyId,
  open,
  onClose,
  onSuccess,
  existingMembers,
}: AddCompanyMemberModalProps): React.ReactElement {
  const formId = useId()
  const addMember = useAddCompanyMember()
  const { data: usersData } = useUsersList({ limit: 100 }, { enabled: open })
  const existingIds = useMemo(() => getMemberUserIds(existingMembers), [existingMembers])
  const availableUsers = useMemo(() => {
    const list = usersData?.data ?? []
    return list.filter((u) => !existingIds.includes(u._id))
  }, [usersData, existingIds])

  const { can } = usePermissions()
  // Same rule as the API: rate overrides need payroll:run or users:view_sensitive.
  const canSetRates = can('payroll', 'run') || can('users', 'view_sensitive')

  const form = useForm<AddCompanyMemberFormValues>({
    resolver: zodResolver(addCompanyMemberSchema),
    defaultValues: {
      userId: '',
      status: 'active',
      role: 'member',
      setOverride: false,
      override: {
        compensationType: 'salaried',
      },
    },
  })
  const { control, handleSubmit, reset, watch, setValue } = form
  const setOverride = watch('setOverride') ?? false
  const overrideType = watch('override.compensationType') ?? 'salaried'

  const onSubmit = (values: AddCompanyMemberFormValues): void => {
    const payload: AddCompanyMemberPayload = {
      userId: values.userId,
      status: values.status,
      role: values.role,
    }
    if (canSetRates && values.setOverride && values.override) {
      payload.compensationOverride =
        values.override.compensationType === 'hourly'
          ? {
              compensationType: 'hourly',
              hourlyRate: values.override.hourlyRate as number,
            }
          : {
              compensationType: 'salaried',
              baseSalary: values.override.baseSalary as number,
            }
    }
    addMember.mutate(
      { companyId, payload },
      {
        onSuccess: () => {
          reset()
          onSuccess()
          onClose()
        },
      }
    )
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="flex w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] flex-col gap-0 overflow-y-auto p-0 sm:w-full sm:max-w-lg">
        <Form {...form}>
          <form id={formId} onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
            <DialogHeader>
              <DialogTitle>Add member</DialogTitle>
              <DialogDescription>
                Choose a user to add to this company. They can be scheduled on shifts and will appear in
                company attendance.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-5 px-4 py-4 sm:px-6 sm:py-5">
              <FormField
                control={control}
                name="userId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>User</FormLabel>
                    <Select
                      options={availableUsers.map((u) => ({
                        value: u._id,
                        label: u.name || u.email,
                        description: userSubline(u.employeeId, u.email),
                      }))}
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      placeholder="Select user…"
                      disabled={availableUsers.length === 0}
                      clearable={false}
                      searchable
                      searchPlaceholder="Search users…"
                    />
                    {availableUsers.length === 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        No users available to add (all may already be members).
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role</FormLabel>
                    <SegmentedControl
                      ariaLabel="Role"
                      className="w-full"
                      options={[
                        { value: 'member', label: 'Member', icon: User },
                        { value: 'manager', label: 'Manager', icon: ShieldCheck },
                      ]}
                      value={field.value ?? 'member'}
                      onChange={field.onChange}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <SegmentedControl
                      ariaLabel="Status"
                      className="w-full"
                      options={[
                        { value: 'active', label: 'Active', icon: UserCheck },
                        { value: 'inactive', label: 'Inactive', icon: CircleSlash },
                      ]}
                      value={field.value ?? 'active'}
                      onChange={field.onChange}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              {canSetRates && (
                <div className="grid gap-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Compensation
                  </span>
                  <SegmentedControl
                    ariaLabel="Compensation source"
                    variant="cards"
                    options={[
                      {
                        value: 'inherit',
                        label: "Inherit from user's profile",
                        description:
                          'Payroll uses the rate already set on the user.',
                      },
                      {
                        value: 'override',
                        label: 'Set a company-specific rate',
                        description:
                          'Overrides the default when payroll runs for this company. Required if the user works for more than one company.',
                      },
                    ]}
                    value={setOverride ? 'override' : 'inherit'}
                    onChange={(next) =>
                      setValue('setOverride', next === 'override', {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                  />

                  {setOverride && (
                    <div className="mt-1 space-y-3 rounded-xl border border-gray-200 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-900/30">
                      <FormField
                        control={control}
                        name="override.compensationType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Pay type</FormLabel>
                            <SegmentedControl
                              ariaLabel="Pay type"
                              className="w-full"
                              options={[
                                { value: 'salaried', label: 'Salaried' },
                                { value: 'hourly', label: 'Hourly' },
                              ]}
                              value={field.value ?? 'salaried'}
                              onChange={(v) => {
                                field.onChange(v)
                                // Clear the now-invalid rate field on switch.
                                if (v === 'hourly') {
                                  setValue('override.baseSalary', undefined, {
                                    shouldValidate: true,
                                  })
                                } else {
                                  setValue('override.hourlyRate', undefined, {
                                    shouldValidate: true,
                                  })
                                }
                              }}
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {overrideType === 'hourly' ? (
                        <FormField
                          control={control}
                          name="override.hourlyRate"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Hourly rate</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={
                                    field.value === undefined ||
                                    Number.isNaN(field.value)
                                      ? ''
                                      : field.value
                                  }
                                  onChange={(e) => {
                                    const v = e.target.value
                                    field.onChange(v === '' ? undefined : Number(v))
                                  }}
                                  placeholder="0.00"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      ) : (
                        <FormField
                          control={control}
                          name="override.baseSalary"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Base salary</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={
                                    field.value === undefined ||
                                    Number.isNaN(field.value)
                                      ? ''
                                      : field.value
                                  }
                                  onChange={(e) => {
                                    const v = e.target.value
                                    field.onChange(v === '' ? undefined : Number(v))
                                  }}
                                  placeholder="0.00"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <DialogFooter className="px-4 py-4 sm:px-6">
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                className="w-full sm:w-auto"
                form={formId}
                type="submit"
                disabled={availableUsers.length === 0} loading={addMember.isPending}
              >
                {addMember.isPending ? 'Adding…' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

AddCompanyMemberModal.displayName = 'AddCompanyMemberModal'
